import React, { useState, useEffect } from 'react';
import { ManagedImage } from '../components/ManagedImage';
import { fetchSiteImages } from '../lib/portfolio';
import { fetchSiteTexts, fallbackTexts } from '../lib/texts';
import { fallbackSiteImage } from '../lib/fallback';

const timeline = [
  {
    marker: '2010',
    title: 'Återbrukets början',
    text: 'Under Shabby Chic-eran startade jag min resa med att måla om möbler för min gårdsbutik White Romance. Idag har den vita trenden fått ge plats åt både färg och naturliga trätoner.',
  },
  {
    marker: 'II',
    italic: true,
    title: 'Färg & Form',
    text: 'Min känsla för färg och stämning har lett mig in i festernas värld. Jag älskar att skapa helhetsupplevelser för bröllop och fest där varje detalj bidrar till att förverkliga visioner.',
  },
  {
    marker: 'III',
    italic: true,
    title: 'Visual Merchandising',
    text: 'Som Visual Merchandiser på Cubus byggde jag upp 21 butiker och utbildade personal i butikskommunikation och exponering.',
  },
];

const skills = [
  { title: 'Visual Merchandiser', sub: 'Dekoratör med 25+ års erfarenhet' },
  { title: 'Egen gårdsbutik', sub: 'White Romance 4 år' },
  { title: 'Egen butik', sub: 'Krusidull (barnkläder, skor, leksaker) 6 år' },
  { title: 'Färg- & Formlära', sub: 'Grundutbildning inom design' },
  { title: 'Grafisk Design', sub: 'Logotyper och visuell identitet' },
  { title: 'Butikskommunikation', sub: 'Exponering och kundflöde' },
  { title: 'Eventdesign', sub: 'Bröllop och festdukning' },
];

export const AboutPage = () => {
  const [portrait, setPortrait] = useState(() => fallbackSiteImage('about_portrait'));
  const [texts, setTexts] = useState(fallbackTexts);

  useEffect(() => {
    let mounted = true;
    fetchSiteImages().then((imgs) => mounted && imgs.about_portrait && setPortrait(imgs.about_portrait));
    fetchSiteTexts().then((t) => mounted && setTexts(t));
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="bg-cv-base min-h-screen" style={{ padding: 'clamp(120px,18vw,150px) clamp(20px,5vw,32px) clamp(64px,10vw,96px)' }}>
      <div className="max-w-[1100px] mx-auto">
        {/* Porträtt + introduktion */}
        <div className="grid items-center" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,320px),1fr))', gap: 'clamp(40px,6vw,72px)', marginBottom: 'clamp(64px,10vw,96px)' }}>
          <div className="relative">
            <span className="absolute border border-cv-edge2" style={{ top: -18, left: -18, right: 18, bottom: 18, borderRadius: 'var(--cv-radius)' }} />
            <ManagedImage
              url={portrait.url}
              basePath={portrait.basePath}
              filename={portrait.filename}
              alt="Charlott Hill - CHills Visuella"
              fit={portrait.fit}
              focusX={portrait.focusX}
              focusY={portrait.focusY}
              className="relative w-full"
              style={{ aspectRatio: '3/4', borderRadius: 'var(--cv-radius)', boxShadow: '0 24px 60px rgba(46,42,36,0.16)' }}
            />
          </div>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4">
              <span className="text-xs tracking-[0.34em] uppercase text-cv-accent">Om mig</span>
              <h1 className="m-0 font-serif font-normal text-cv-ink" style={{ fontSize: 'clamp(34px,6vw,56px)' }}>Charlott Hill</h1>
              <span className="w-14 h-px bg-cv-accent" />
            </div>
            <p className="m-0 font-serif text-[23px] leading-[1.55] text-cv-ink2">{texts.about_lead}</p>
            <p className="m-0 text-base leading-[1.75] text-cv-muted">{texts.about_text}</p>
            <div className="grid grid-cols-2 gap-6 mt-3 py-7 border-t border-b border-cv-border">
              <div className="text-center flex flex-col gap-1.5">
                <span className="font-serif text-cv-ink" style={{ fontSize: 40 }}>25+</span>
                <span className="text-xs tracking-[0.24em] uppercase text-cv-faint">År i branschen</span>
              </div>
              <div className="text-center flex flex-col gap-1.5">
                <span className="font-serif text-cv-ink" style={{ fontSize: 40 }}>21</span>
                <span className="text-xs tracking-[0.24em] uppercase text-cv-faint">Butiker byggda</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tidslinje */}
        <div className="mb-24">
          <h2 className="m-0 mb-14 font-serif font-medium text-cv-ink text-center" style={{ fontSize: 'clamp(28px,4.5vw,38px)' }}>Min kreativa resa</h2>
          <div className="grid gap-10" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,260px),1fr))' }}>
            {timeline.map((t) => (
              <div key={t.title} className="flex flex-col gap-3.5">
                <span
                  className={`w-[52px] h-[52px] border border-cv-accent rounded-full flex items-center justify-center font-serif text-cv-accent ${t.italic ? 'italic' : ''}`}
                  style={{ fontSize: t.italic ? 18 : 17 }}
                >
                  {t.marker}
                </span>
                <h3 className="m-0 font-serif font-medium text-2xl text-cv-ink">{t.title}</h3>
                <p className="m-0 text-[15px] leading-[1.7] text-cv-muted">{t.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Filosofi */}
        <div className="bg-cv-panel text-center mb-24" style={{ borderRadius: 'var(--cv-radius)', padding: 'clamp(48px,8vw,72px) clamp(24px,5vw,48px)' }}>
          <div className="max-w-[760px] mx-auto flex flex-col items-center gap-5">
            <h2 className="m-0 font-serif font-medium text-cv-ink" style={{ fontSize: 'clamp(28px,4.5vw,38px)' }}>Min filosofi</h2>
            <span className="w-14 h-px bg-cv-accent" />
            <blockquote className="m-0 font-serif italic text-cv-ink2" style={{ fontSize: 'clamp(21px,3vw,26px)', lineHeight: 1.5 }}>
              "Återbruk ligger mig varmt om hjärtat. Jag tror på att ge nytt liv till gamla ting – att bevara, förädla och skapa något unikt."
            </blockquote>
            <p className="m-0 text-[15px] leading-[1.75] text-cv-muted max-w-[60ch]">
              Oavsett om det handlar om en möbelrenovering, festdukning eller skyltfönsterexponering, drivs jag av samma sak – att skapa något som fångar ögat, väcker känslor och berättar en historia.
            </p>
          </div>
        </div>

        {/* Kompetenser */}
        <div className="mb-24">
          <h2 className="m-0 mb-14 font-serif font-medium text-cv-ink text-center" style={{ fontSize: 'clamp(28px,4.5vw,38px)' }}>Kompetenser &amp; Erfarenhet</h2>
          <div className="max-w-[860px] mx-auto grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,300px),1fr))', gap: '28px 56px' }}>
            {skills.map((s) => (
              <div key={s.title} className="flex gap-4 items-start border-b border-cv-border pb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-cv-accent mt-[9px] flex-shrink-0" />
                <div className="flex flex-col gap-0.5">
                  <h3 className="m-0 font-normal text-base text-cv-ink">{s.title}</h3>
                  <p className="m-0 text-sm text-cv-faint">{s.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Hill Service AB */}
        <div className="border border-cv-border text-center" style={{ borderRadius: 'var(--cv-radius)', padding: 'clamp(40px,7vw,56px) clamp(24px,5vw,48px)' }}>
          <div className="max-w-[640px] mx-auto flex flex-col gap-4">
            <h2 className="m-0 font-serif font-medium text-cv-ink" style={{ fontSize: 'clamp(26px,4vw,32px)' }}>Hill Service AB</h2>
            <p className="m-0 text-[15px] leading-[1.75] text-cv-muted">
              Hill Service AB ägs av Ingemar och Charlott Hill. Bolaget har olika verksamhetsgrenar, där CHills Visuella är Charlotts kreativa del som du ser på denna sida. Ingemars del av verksamheten är inom åkeribranschen.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
